import {
  BundleFreeTrial,
  SubscriptionBundlePrice,
  SubscriptionBundle,
  RawSubscriptionTier,
  Discount,
  SubscriptionProviderPriceIds,
} from '../../common/bundle.types';
import {
  UpsertOrArchiveSubscriptionBundlePricesInput,
  CouponProductType,
  BillingCycleInterval,
} from 'generated/graphql';
import { isEqual, isNil } from 'lodash';

import { convertDollarsToCents } from '../../common/bundles.mapper';
import { UseFormSetValue } from 'react-hook-form';
import { BundleFormValues } from './editBundlePrice.vm';

export const getPricesFromSubscriptionTier = (
  subscriptionTier: RawSubscriptionTier,
): SubscriptionBundlePrice => {
  const monthlyPrice = subscriptionTier?.prices?.find(
    (p) => p.billingCycleInterval === BillingCycleInterval.Month,
  );
  const annualPrice = subscriptionTier?.prices?.find(
    (p) => p.billingCycleInterval === BillingCycleInterval.Year,
  );

  return {
    monthly: monthlyPrice?.price ? monthlyPrice.price / 100 : undefined,
    annual: annualPrice?.price ? annualPrice.price / 100 : undefined,
  };
};

export const getFreeTrialFromSubscriptionTier = (
  subscriptionTier: RawSubscriptionTier,
) => {
  const monthlyPrice = subscriptionTier?.prices?.find(
    (p) => p.billingCycleInterval === BillingCycleInterval.Month,
  );
  const annualPrice = subscriptionTier?.prices?.find(
    (p) => p.billingCycleInterval === BillingCycleInterval.Year,
  );

  return {
    monthly: !!monthlyPrice?.isFreeTrialEnabled,
    annual: !!annualPrice?.isFreeTrialEnabled,
  };
};

export const getProviderPriceIds = (
  subscriptionTier: RawSubscriptionTier,
): SubscriptionProviderPriceIds => {
  const monthlyPrice = subscriptionTier?.prices?.find(
    (p) => p.billingCycleInterval === BillingCycleInterval.Month,
  );
  const annualPrice = subscriptionTier?.prices?.find(
    (p) => p.billingCycleInterval === BillingCycleInterval.Year,
  );

  return {
    monthly: monthlyPrice?.providerPriceId,
    annual: annualPrice?.providerPriceId,
  };
};

export const mapPriceFormDataToInput = (
  bundle: SubscriptionBundle,
  values: BundleFormValues,
): UpsertOrArchiveSubscriptionBundlePricesInput => {
  const freeTrial = {
    monthly: !values.monthlyPriceEnabled ? false : values.monthlyFreeTrial,
    annual: !values.annualPriceEnabled ? false : values.annualFreeTrial,
  };

  return {
    bundleId: bundle.id,
    freeTrial,
    annualPriceCents: mapPriceValueForInput({
      updatePriceEnabled: values.annualPriceEnabled,
      updatePrice: values.annualPrice,
    }),
    monthlyPriceCents: mapPriceValueForInput({
      updatePriceEnabled: values.monthlyPriceEnabled,
      updatePrice: values.monthlyPrice,
    }),
  };
};

export const mapPriceValueForInput = ({
  updatePriceEnabled,
  updatePrice,
}: {
  updatePriceEnabled: boolean;
  updatePrice?: string;
}): number | null => {
  if (updatePriceEnabled && updatePrice) {
    return convertDollarsToCents(updatePrice);
  }

  return null;
};

export const mapBundleFormData = (
  bundle: SubscriptionBundle,
): BundleFormValues => ({
  monthlyPriceEnabled: !!bundle.price.monthly,
  annualPriceEnabled: !!bundle.price.annual,
  monthlyPrice: bundle.price.monthly ? String(bundle.price.monthly) : undefined,
  annualPrice: bundle.price.annual ? String(bundle.price.annual) : undefined,
  monthlyFreeTrial: !!bundle.freeTrial.monthly,
  annualFreeTrial: !!bundle.freeTrial.annual,
});

export const mapDiscounts = ({
  editBundle,
  discounts,
}: {
  editBundle?: SubscriptionBundle;
  discounts?: Discount[];
}): {
  monthly: number;
  annual: number;
} | null => {
  if (isNil(editBundle) || isNil(discounts)) return null;

  const monthly = discounts.filter(
    (d) =>
      d.isActive &&
      d.productType === CouponProductType.Subscription &&
      d.subscriptionProviderPriceId === editBundle.providerPriceId?.monthly,
  ).length;
  const annual = discounts.filter(
    (d) =>
      d.isActive &&
      d.productType === CouponProductType.Subscription &&
      d.subscriptionProviderPriceId === editBundle.providerPriceId?.annual,
  ).length;

  if (monthly === 0 && annual === 0) return null;

  return {
    monthly,
    annual,
  };
};

export const priceRange = {
  monthlyPrice: {
    min: 1,
    max: 1000,
  },
  annualPrice: {
    min: 10,
    max: 12000,
  },
};

export type PriceRangeType = keyof Pick<
  BundleFormValues,
  'monthlyPrice' | 'annualPrice'
>;

export const transformPrice = (
  value: string,
  type: PriceRangeType,
  callback: UseFormSetValue<BundleFormValues>,
): void => {
  if (Number(value) < priceRange[type].min) {
    callback(type, String(priceRange[type].min));
  }
  if (Number(value) > priceRange[type].max) {
    callback(type, String(priceRange[type].max));
  }
};

export const getPriceChange = ({
  monthlyPriceEnabled,
  monthlyPrice,
  annualPriceEnabled,
  annualPrice,
  bundlePrice,
}: { bundlePrice?: SubscriptionBundle['price'] } & Pick<
  BundleFormValues,
  'annualPriceEnabled' | 'monthlyPriceEnabled' | 'annualPrice' | 'monthlyPrice'
>): {
  monthly?: string;
  annual?: string;
} => ({
  monthly:
    monthlyPriceEnabled &&
    !!bundlePrice?.monthly &&
    !!monthlyPrice &&
    bundlePrice.monthly !== Number(monthlyPrice)
      ? monthlyPrice
      : undefined,
  annual:
    annualPriceEnabled &&
    !!bundlePrice?.annual &&
    !!annualPrice &&
    bundlePrice.annual !== Number(annualPrice)
      ? annualPrice
      : undefined,
});

export const getPriceDisabled = ({
  monthlyPriceEnabled,
  annualPriceEnabled,
  bundlePrice,
}: { bundlePrice?: SubscriptionBundle['price'] } & Pick<
  BundleFormValues,
  'annualPriceEnabled' | 'monthlyPriceEnabled'
>): {
  monthly: boolean;
  annual: boolean;
} => ({
  monthly: !monthlyPriceEnabled && !!bundlePrice?.monthly,
  annual: !annualPriceEnabled && !!bundlePrice?.annual,
});
